import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    "Content-Type": "application/json"
  },
  timeout: 60000 // 60 second timeout
});

// Helper function to extract error message from API response
// Handles both string and object error details (e.g., validation errors with {type, loc, msg, input, url})
export const getErrorMessage = (error, fallbackMessage = "Ein Fehler ist aufgetreten") => {
  // Check for 503 (Database connection) errors first
  if (error?.response?.status === 503) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return "Server vorübergehend nicht erreichbar. Bitte versuchen Sie es in wenigen Sekunden erneut.";
  }
  
  // Check for network errors (no response at all)
  if (error?.message === 'Network Error') {
    return "Serververbindung unterbrochen - Bitte versuchen Sie es in wenigen Sekunden erneut. (Falls das Problem bestehen bleibt, laden Sie die Seite neu.)";
  }
  
  // Check for timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return "Zeitüberschreitung - Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut";
  }
  
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    // Handle Pydantic/FastAPI validation error format
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    return detail.msg || detail.message || fallbackMessage;
  }
  return error?.message || fallbackMessage;
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors with auto-retry for 503 (DB connection)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    
    // --- Network Error / Cloudflare 520 Retry ---
    // These are transient: server restart, PDF generation timeout, DB reconnect
    const isNetworkError = !error.response && error.message === 'Network Error';
    const isCloudflareError = error.response?.status >= 520 && error.response?.status <= 530;
    const isTimeout = error.code === 'ECONNABORTED';
    
    if ((isNetworkError || isCloudflareError || isTimeout) && !config.__skipRetry) {
      const retryCount = config._networkRetryCount || 0;
      if (retryCount < 2) {
        config._networkRetryCount = retryCount + 1;
        const delay = (retryCount + 1) * 2000; // 2s, 4s
        console.warn(`[API] Network error, retry ${retryCount + 1}/2 in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return api.request(config);
      }
    }
    
    // --- 401 Handling (auth errors) ---
    if (error.response?.status === 401) {
      const url = config.url || "";
      const isAuthEndpoint = url.includes("/auth/me") || url.includes("/auth/login");
      
      if (!isAuthEndpoint) {
        // For non-auth endpoints, retry ONCE (handles server restart mid-request)
        const authRetry = config._authRetryCount || 0;
        if (authRetry < 1) {
          config._authRetryCount = authRetry + 1;
          await new Promise(resolve => setTimeout(resolve, 1000));
          return api.request(config);
        }
      }
      
      // After retry or on auth endpoints, logout
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    
    // --- 503 Retry (DB reconnection) ---
    const retryCount503 = config._retryCount || 0;
    if (error.response?.status === 503 && retryCount503 < 2) {
      config._retryCount = retryCount503 + 1;
      const delay = (retryCount503 + 1) * 1500;
      await new Promise(resolve => setTimeout(resolve, delay));
      return api.request(config);
    }
    
    return Promise.reject(error);
  }
);

// API Functions
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
  selectCompany: (companyId) => api.post("/auth/select-company", { company_id: companyId })
};

export const companiesAPI = {
  list: () => api.get("/companies/"),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post("/companies/", data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  assignAdmin: (companyId, userId) => api.post(`/companies/${companyId}/assign-admin`, null, { params: { user_id: userId } }),
  removeUser: (companyId, userId) => api.post(`/companies/${companyId}/remove-user`, null, { params: { user_id: userId } }),
  uploadLogo: (companyId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/companies/${companyId}/upload-logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteLogo: (companyId) => api.delete(`/companies/${companyId}/logo`),
  uploadCoverImage: (companyId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/companies/${companyId}/upload-cover-image`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteCoverImage: (companyId) => api.delete(`/companies/${companyId}/cover-image`),
  getCoverPageConfig: (companyId) => api.get(`/companies/${companyId}/cover-page-config`),
  updateCoverPageConfig: (companyId, config) => api.put(`/companies/${companyId}/cover-page-config`, config),
  exportCoverPageConfig: (companyId) => api.get(`/companies/${companyId}/cover-page-config/export`),
  importCoverPageConfig: (companyId, formData) => api.post(`/companies/${companyId}/cover-page-config/import`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  getFinancingPageConfig: (companyId) => api.get(`/companies/${companyId}/financing-page-config`),
  updateFinancingPageConfig: (companyId, config) => api.put(`/companies/${companyId}/financing-page-config`, config),
  updateCI: (companyId, data) => api.post(`/companies/${companyId}/ci`, data),
  getEmailSettings: (companyId) => api.get(`/companies/${companyId}/email-settings`),
  updateEmailSettings: (companyId, data) => api.put(`/companies/${companyId}/email-settings`, data),
  testEmail: (companyId) => api.post(`/companies/${companyId}/test-email`),
  // VAT settings
  getVatSettings: (companyId) => api.get(`/companies/${companyId}/vat-settings`),
  updateVatSettings: (companyId, data) => api.put(`/companies/${companyId}/vat-settings`, data),
  // AB (Auftragsbestätigung) text blocks – dedizierter Endpoint, schreibt nur die AB-Felder
  updateAbTextBlocks: (companyId, data) => api.put(`/companies/${companyId}/ab-text-blocks`, data),
  // Export functions
  exportFullDatabase: () => api.get("/companies/export/full-database"),
  exportCompany: (companyId) => api.get(`/companies/${companyId}/export`),
  // Import functions
  importFullDatabase: (formData) => api.post("/companies/import/full-database", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  importCompany: (companyId, formData) => api.post(`/companies/${companyId}/import`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  validateImport: (formData) => api.post("/companies/import/validate", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })
};

export const usersAPI = {
  list: () => api.get("/users/"),
  create: (data) => api.post("/users/", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateProfile: (data) => api.put("/users/me/profile", data),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/users/me/upload-photo", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deletePhoto: () => api.delete("/users/me/photo")
};

export const customersAPI = {
  list: () => api.get("/customers/"),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post("/customers/", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/customers/import/csv", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  }
};

export const projectsAPI = {
  list: (status, includeArchived = false, customerId = null) => api.get("/projects/", { 
    params: { 
      status, 
      include_archived: includeArchived,
      customer_id: customerId 
    } 
  }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post("/projects/", data),
  updateStatus: (id, data) => api.put(`/projects/${id}/status`, data),
  markOfferAccepted: (id, offerId = null) => api.post(`/projects/${id}/mark-offer-accepted`, null, { 
    params: offerId ? { offer_id: offerId } : {} 
  }),
  archive: (id) => api.post(`/projects/${id}/archive`),
  unarchive: (id) => api.post(`/projects/${id}/unarchive`),
  getOrderConfirmationPdfUrl: (id, showItemPrices) => {
    const token = localStorage.getItem("token");
    const timestamp = Date.now();
    const sip = showItemPrices === undefined || showItemPrices === null ? "" : `&show_item_prices=${showItemPrices}`;
    return `${BACKEND_URL}/api/projects/${id}/order-confirmation-pdf?token=${token}&t=${timestamp}${sip}`;
  },
  generateOrderConfirmation: (id, showItemPrices) => {
    const params = (showItemPrices === undefined || showItemPrices === null) ? "" : `?show_item_prices=${showItemPrices}`;
    return api.post(`/projects/${id}/generate-order-confirmation${params}`);
  },
  sendOrderConfirmation: (id) => api.post(`/projects/${id}/send-order-confirmation`),
  // Anlagentyp + Meldungs-Felder (Einspeiseanfrage / MaStR / Fertigmeldung / §14a)
  updateRegistrations: (id, data) => api.patch(`/projects/${id}/registrations`, data)
};

export const offersAPI = {
  list: (projectId, includeArchived = false) => api.get("/offers/", { params: { project_id: projectId, include_archived: includeArchived } }),
  get: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post("/offers/", data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  sign: (id, signatureData) => api.post(`/offers/${id}/sign`, { signature_data: signatureData }),
  updateStatus: (id, status) => api.put(`/offers/${id}/status`, null, { params: { status } }),
  archive: (id) => api.post(`/offers/${id}/archive`),
  unarchive: (id) => api.post(`/offers/${id}/unarchive`),
  uploadImages: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/offers/${id}/upload-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteImages: (id) => api.delete(`/offers/${id}/images`),
  getPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    const timestamp = Date.now(); // Cache-buster
    return `${BACKEND_URL}/api/offers/${id}/pdf?token=${token}&t=${timestamp}`;
  },
  downloadPdf: async (id) => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${BACKEND_URL}/api/offers/${id}/pdf?token=${token}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    if (!response.ok) throw new Error('PDF konnte nicht geladen werden');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
  getContractPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    const timestamp = Date.now();
    return `${BACKEND_URL}/api/offers/${id}/contract-pdf?token=${token}&t=${timestamp}`;
  },
  generateContract: (id, signatureData) => api.post(`/offers/${id}/generate-contract`, { signature_data: signatureData }),
  sendOffer: (id) => api.post(`/offers/${id}/send-offer`, null, { timeout: 120000 }),
  refreshPdf: (id) => api.post(`/offers/${id}/refresh-pdf`),
  sendEmail: (id) => api.post(`/offers/${id}/send-offer`, null, { timeout: 120000 }),  // Alias for sendOffer
  sendContract: (id) => api.post(`/offers/${id}/send-contract`, null, { timeout: 120000 }),
  // Remote Digital Signing
  sendForSignature: (id, data) => api.post(`/offers/${id}/send-for-signature`, data),
};

export const componentsAPI = {
  list: (type, includeHidden = false) => api.get("/components/", { params: { type, include_hidden: includeHidden } }),
  get: (id) => api.get(`/components/${id}`),
  create: (data) => api.post("/components/", data),
  update: (id, data) => api.put(`/components/${id}`, data),
  delete: (id) => api.delete(`/components/${id}`),
  copy: (id) => api.post(`/components/${id}/copy`),
  toggleVisibility: (id) => api.post(`/components/${id}/toggle-visibility`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/components/${id}/upload-image`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteImage: (id) => api.delete(`/components/${id}/image`),
  uploadDatasheet: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/components/${id}/upload-datasheet`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  deleteDatasheet: (id) => api.delete(`/components/${id}/datasheet`),
  getDatasheetUrl: (id) => {
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/components/${id}/datasheet?token=${token}`;
  }
};

export const priceMatrixAPI = {
  list: () => api.get("/price-matrix/"),
  get: (id) => api.get(`/price-matrix/${id}`),
  create: (data) => api.post("/price-matrix/", data),
  update: (id, data) => api.put(`/price-matrix/${id}`, data)
};

export const installationCostsAPI = {
  get: () => api.get("/installation-costs/"),
  update: (data) => api.put("/installation-costs/", data),
  getAllCompanies: () => api.get("/installation-costs/all-companies"),
  copy: (sourceCompanyId, targetCompanyIds) => api.post("/installation-costs/copy", {
    source_company_id: sourceCompanyId,
    target_company_ids: targetCompanyIds
  })
};

export const marginAPI = {
  getOfferMargin: (offerId) => api.get(`/margin/offer/${offerId}`),
  getOverview: (status = "signed") => api.get("/margin/overview", { params: { status } })
};

export const seasonalDiscountsAPI = {
  list: () => api.get("/seasonal-discounts/"),
  getActive: () => api.get("/seasonal-discounts/active"),
  create: (data) => api.post("/seasonal-discounts/", data),
  update: (id, data) => api.put(`/seasonal-discounts/${id}`, data),
  delete: (id) => api.delete(`/seasonal-discounts/${id}`)
};

export const planningAPI = {
  list: () => api.get("/planning/"),
  get: (id) => api.get(`/planning/${id}`),
  getByProject: (projectId) => api.get(`/planning/project/${projectId}`),
  create: (data) => api.post("/planning/", data),
  update: (id, data) => api.put(`/planning/${id}`, data)
};

export const textBlocksAPI = {
  list: (type) => api.get("/text-blocks/", { params: { type } }),
  create: (data) => api.post("/text-blocks/", data),
  update: (id, data) => api.put(`/text-blocks/${id}`, data),
  delete: (id) => api.delete(`/text-blocks/${id}`),
  toggleActive: (id) => api.patch(`/text-blocks/${id}/toggle-active`)
};

export const pipelineAPI = {
  getStages: () => api.get("/pipeline/stages"),
  get: () => api.get("/pipeline/"),
  move: (projectId, newStatus) => api.put(`/pipeline/${projectId}/move`, null, { params: { new_status: newStatus } })
};

export const invoicesAPI = {
  list: (params = {}) => api.get("/invoices/", { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices/", data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  finalize: (id) => api.post(`/invoices/${id}/finalize`),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  unlock: (id) => api.post(`/invoices/${id}/unlock`),
  verify: (id) => api.get(`/invoices/${id}/verify`),
  sendEmail: (id) => api.post(`/invoices/${id}/send`),
  markAsPaid: (id, data = {}) => api.post(`/invoices/${id}/mark-paid`, data),
  getAuditLog: (params = {}) => api.get("/invoices/audit-log/", { params }),
  export: (data) => api.post("/invoices/export", data),
  exportAll: () => api.get("/invoices/export-all", { responseType: 'blob' }),
  exportWithDateRange: (data) => api.post("/invoices/export", data),
  // Neu: Schlussrechnung neu berechnen
  recalculateFinal: (id) => api.post(`/invoices/${id}/recalculate-final`),
  getPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    const timestamp = Date.now();
    return `${BACKEND_URL}/api/invoices/${id}/pdf?token=${token}&t=${timestamp}`;
  },
  getPreviewPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    const timestamp = Date.now();
    return `${BACKEND_URL}/api/invoices/${id}/preview-pdf?token=${token}&t=${timestamp}`;
  },
  getXmlUrl: (id) => {
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/invoices/${id}/xml?token=${token}`;
  }
};

export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats")
};

export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  getFileUrl: (fileId) => `${BACKEND_URL}/api/files/${fileId}`
};

export const seedAPI = {
  seed: () => api.post("/seed")
};

export const adminAPI = {
  previewReset: () => api.get("/admin/reset-preview"),
  productionReset: (confirmCode) => api.post("/admin/production-reset", { confirm_code: confirmCode }),
  createMissingProjects: () => api.post("/admin/create-missing-projects")
};

// Free Offers API (Admin only)
export const freeOffersAPI = {
  list: () => api.get("/free-offers/"),
  get: (id) => api.get(`/free-offers/${id}`),
  create: (data) => api.post("/free-offers/", data),
  update: (id, data) => api.put(`/free-offers/${id}`, data),
  finalize: (id) => api.post(`/free-offers/${id}/finalize`),
  sendEmail: (id) => api.post(`/free-offers/${id}/send`, null, { timeout: 120000 }),
  sendForSignature: (id, data) => api.post(`/free-offers/${id}/send-for-signature`, data, { timeout: 120000 }),
  markAccepted: (id) => api.post(`/free-offers/${id}/mark-accepted`),
  getPdfUrl: (id, showItemPrices = true) => {
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/free-offers/${id}/pdf?token=${token}&show_item_prices=${showItemPrices}`;
  }
};

// Free Invoices API (Admin only)
export const freeInvoicesAPI = {
  list: () => api.get("/free-invoices/"),
  get: (id) => api.get(`/free-invoices/${id}`),
  create: (data) => api.post("/free-invoices/", data),
  createFromOffer: (data) => api.post("/free-invoices/from-offer", data),
  update: (id, data) => api.put(`/free-invoices/${id}`, data),
  finalize: (id) => api.post(`/free-invoices/${id}/finalize`),
  markPaid: (id) => api.post(`/free-invoices/${id}/mark-paid`),
  cancel: (id, reason) => api.post(`/free-invoices/${id}/cancel`, { reason }),
  unlock: (id) => api.post(`/free-invoices/${id}/unlock`),
  sendEmail: (id) => api.post(`/free-invoices/${id}/send`),
  getPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/free-invoices/${id}/pdf?token=${token}`;
  }
};

// Free Offer Templates API (Admin only)
export const freeOfferTemplatesAPI = {
  list: () => api.get("/free-offer-templates/"),
  get: (id) => api.get(`/free-offer-templates/${id}`),
  create: (data) => api.post("/free-offer-templates/", data),
  update: (id, data) => api.put(`/free-offer-templates/${id}`, data),
  delete: (id) => api.delete(`/free-offer-templates/${id}`)
};

// Heat Pump (WP) Offers API (Admin only)
export const wpOffersAPI = {
  list: () => api.get("/wp-offers/"),
  get: (id) => api.get(`/wp-offers/${id}`),
  create: (data) => api.post("/wp-offers/", data),
  update: (id, data) => api.put(`/wp-offers/${id}`, data),
  updateItemGroup: (id, itemIndex, productGroup) =>
    api.patch(`/wp-offers/${id}/items/${itemIndex}/product-group`, { product_group: productGroup }),
  getProfitability: (id) => api.get(`/wp-offers/${id}/profitability`),
  upsertProfitability: (id, data) => api.post(`/wp-offers/${id}/profitability`, data),
  markAccepted: (id) => api.post(`/wp-offers/${id}/mark-accepted`),
  duplicate: (id) => api.post(`/wp-offers/${id}/duplicate`),
  delete: (id) => api.delete(`/wp-offers/${id}`),
  // Templates
  listTemplates: () => api.get(`/wp-offers/templates/`),
  getTemplate: (tid) => api.get(`/wp-offers/templates/${tid}`),
  createTemplate: (data) => api.post(`/wp-offers/templates/`, data),
  updateTemplate: (tid, data) => api.put(`/wp-offers/templates/${tid}`, data),
  deleteTemplate: (tid) => api.delete(`/wp-offers/templates/${tid}`),
  sendEmail: (id) => api.post(`/wp-offers/${id}/send`, null, { timeout: 30000 }),
  getPdfUrl: (id) => {
    const token = localStorage.getItem("token");
    return `${BACKEND_URL}/api/wp-offers/${id}/pdf?token=${token}`;
  }
};

// Reonic Import API (Admin only)
export const reonicAPI = {
  health: () => api.get("/reonic/health"),
  healthExtended: () => api.get("/reonic/health-extended"),
  listOffers: (page = 0) => api.get(`/reonic/offers?page=${page}`),
  preview: (reonicOfferId) => api.get(`/reonic/offers/${reonicOfferId}/preview`),
  import: (reonicOfferId, payload) =>
    api.post(`/reonic/import/${reonicOfferId}`, payload, { timeout: 60000 }),
  getSettings: () => api.get("/reonic/settings"),
  updateSettings: (payload) => api.put("/reonic/settings", payload),
  testSettings: (payload) => api.post("/reonic/settings/test", payload),
};

// WP Product Groups (constants, mirrored from backend WP_PRODUCT_GROUPS)
export const WP_PRODUCT_GROUPS = [
  { key: "waermepumpe", label: "Wärmepumpe", description: "Das Herzstück Ihres Heizsystems" },
  { key: "speicher", label: "Speicherlösungen", description: "Pufferspeicher & Brauchwasserspeicher" },
  { key: "hydraulik", label: "Hydraulik & Steuerung", description: "Hydraulische Anbindung, Regelung, Sensorik" },
  { key: "installation", label: "Installation & Montage", description: "Arbeitsleistung & Inbetriebnahme" },
  { key: "zusatz", label: "Zusatzkomponenten & Schutz", description: "Schallschutz, Wartungspakete, Garantieerweiterungen" },
  { key: "sonstiges", label: "Sonstiges", description: "" },
];

// Activity Log API (Admin only)
export const activityLogAPI = {
  list: (params = {}) => api.get("/activity-log/", { params }),
  users: () => api.get("/activity-log/users"),
  stats: (days = 7) => api.get("/activity-log/stats", { params: { days } })
};

// Pricing Engine API
export const pricingEngineAPI = {
  getStatus: () => api.get("/pricing-engine/status"),
  healthCheck: () => api.get("/pricing-engine/health-check"),
  runSmokeTest: (tier = "secure_plus") => api.post(`/pricing-engine/smoke-test?tier=${tier}`),
  getSmokeTests: (limit = 10) => api.get("/pricing-engine/smoke-tests", { params: { limit } }),
  calculatePreview: (moduleCount, tier) => api.post(`/pricing-engine/calculate-preview?module_count=${moduleCount}&tier=${tier}`)
};

// Suppliers API (Lieferanten)
export const suppliersAPI = {
  list: (params = {}) => api.get("/suppliers/", { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post("/suppliers/", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`)
};

// Credit Notes API (Provisionsgutschriften)
export const creditNotesAPI = {
  list: (params = {}) => api.get("/credit-notes/", { params }),
  get: (id) => api.get(`/credit-notes/${id}`),
  create: (data) => api.post("/credit-notes/", data),
  update: (id, data) => api.put(`/credit-notes/${id}`, data),
  delete: (id) => api.delete(`/credit-notes/${id}`),
  finalize: (id) => api.post(`/credit-notes/${id}/finalize`),
  cancel: (id) => api.post(`/credit-notes/${id}/cancel`),
  markPaid: (id) => api.post(`/credit-notes/${id}/mark-paid`),
  downloadPdf: (id) => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' }),
  exportAll: () => api.get("/credit-notes/export", { responseType: 'blob' })
};

// Kunden-Gutschriften (customer_credit_notes) – neue Domain, Nummernkreis GU-YYYY-NNNN
export const customerCreditNotesAPI = {
  list: () => api.get("/customer-credit-notes/"),
  get: (id) => api.get(`/customer-credit-notes/${id}`),
  create: (data) => api.post("/customer-credit-notes/", data),
  update: (id, data) => api.put(`/customer-credit-notes/${id}`, data),
  delete: (id) => api.delete(`/customer-credit-notes/${id}`),
  finalize: (id) => api.post(`/customer-credit-notes/${id}/finalize`),
  cancel: (id) => api.post(`/customer-credit-notes/${id}/cancel`),
  downloadPdf: (id) => api.get(`/customer-credit-notes/${id}/pdf`, { responseType: 'blob' }),
};

export const signingRequestsAPI = {
  list: (params = {}) => api.get("/signing-requests/", { params }),
  get: (id) => api.get(`/signing-requests/${id}`),
  getStats: () => api.get("/signing-requests/stats"),
  getEvents: (id) => api.get(`/signing-requests/${id}/events`),
  revoke: (id) => api.post(`/signing-requests/${id}/revoke`),
  regenerateToken: (id) => api.post(`/signing-requests/${id}/regenerate-token`),
  resend: (id, data = {}) => api.post(`/signing-requests/${id}/resend`, data),
  sendEmail: (id, data = {}) => api.post(`/signing-requests/${id}/resend`, data),  // Alias for resend
};


// Email Tracking API
export const emailTrackingAPI = {
  list: (params = {}) => api.get("/email-tracking/", { params }),
  get: (id) => api.get(`/email-tracking/${id}`),
  getStats: (days = 30) => api.get("/email-tracking/stats", { params: { days } }),
  getFailed: (limit = 50) => api.get("/email-tracking/failed", { params: { limit } }),
  getForEntity: (entityType, entityId) => api.get(`/email-tracking/entity/${entityType}/${entityId}`),
  getUnreadNotifications: () => api.get("/email-tracking/notifications/unread"),
  markNotificationRead: (id) => api.post(`/email-tracking/notifications/${id}/read`)
};

// Legacy Offers API (Freitextangebote)
export const legacyOffersAPI = {
  list: (params = {}) => api.get("/legacy-offers/", { params }),
  get: (id) => api.get(`/legacy-offers/${id}`),
  create: (data) => api.post("/legacy-offers/", data),
  update: (id, data) => api.put(`/legacy-offers/${id}`, data),
  delete: (id) => api.delete(`/legacy-offers/${id}`),
  import: (formData) => api.post("/legacy-offers/import", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  }),
  getPdfUrl: (id) => api.get(`/legacy-offers/${id}/pdf`),
  getHistory: (id) => api.get(`/legacy-offers/${id}/history`),
  createInvoice: (id, data) => api.post(`/legacy-offers/${id}/create-invoice`, data),
  getCustomerSummary: (customerId) => api.get(`/legacy-offers/customer/${customerId}/summary`),
  linkToProject: (projectId, legacyOfferId) => api.put(`/projects/${projectId}/link-legacy-offer?legacy_offer_id=${legacyOfferId}`)
};

